import { ENV } from '../../../config/runtimeEnv';
import React, { useState, useEffect, useMemo, useRef } from "react";
import axios from "axios";
import debounce from "lodash/debounce";
import { getSessionUser } from "../../../utils/auth/sessionUser";
import { can, canUseOperatorTools, canViewPrescriptionDrafts } from "../../../utils/userPrefs/validatePermissions";
import { formatInZone, fromUtc } from "../../../utils/time";

import { Badge, Button, Drawer, Grid, Modal, Select } from "antd";
import { SaveOutlined, LockOutlined, EditOutlined, ReloadOutlined, FileSyncOutlined, DownloadOutlined } from "@ant-design/icons";

const { useBreakpoint } = Grid;

import DetailDrawer from "../../../components/shared/DetailDrawer";

// Api imports
import { activatePatient, deactivatePatient, updatePatient, createPatient, fetchPatient, exportPatientXml } from "../../../api/healthcare/PatientApi";
import { fetchTemplates, Template } from "../../../api/communications/templateApi";
import { fetchPatientOverview } from "../../../api/healthcare/PatientOverviewApi";
import { fetchSentNotifications } from "../../../api/communications/sentNotificationsApi";
import { getAssignedForms } from "../../../api/formApi";
import { listUserFiles, listPatientReports } from "../../../api/file/fileServiceApi";
import { getPatientResourceLinks } from "../../../api/file/patientResourceLinksApi";
import { XmlImportJobStatus } from "../../../api/healthcare/PatientApi";
import { sendCommunication } from "../../../api/communications/communicationApi";
import { SendCommunicationRequest } from "../../../types/communications/CommunicationTypes";
import { fetchClinicCompany } from "../../../api/admin/companyApi";

// Types
import { Patient, isCustomerIdentity } from "../../../types/healthcare/PatientTypes";
import {
	IhiValidationFeedback,
	parseIhiValidationFeedback,
	extractIhiValidationFromMutationError,
} from "../../../utils/healthcare/IhiValidationFeedback";
import {
	CoreDemographicRevalidationOffer,
	describeChangedDemographics,
	hasServerConfirmedVerifiedIhi,
	mergeCoreDemographicRevalidationOffers,
	parseCoreDemographicRevalidationOffer,
} from "../../../utils/healthcare/CoreDemographicIhiRevalidation";
import IhiRevalidationPanel from "../../../components/healthcare/IhiRevalidationPanel";
import { hasServerConfirmedCurrentIhi } from "../../../utils/healthcare/AddressIhiSearch";
import { Test } from "../../../types/healthcare/TestTypes";
import { Medication } from "../../../types/healthcare/MedicationTypes";
import { MedicalHistory } from "../../../types/healthcare/MedicalHistoryTypes";
import { Appointment, CompletedAppointment } from "../../../types/appointments/AppointmentTypes";
import { SentNotification } from "../../../types/communications/NotificationTypes";
import { AssignedForm } from "../../../api/formApi";
import { FileMetadata, ReportFile } from "../../../types/file/FileTypes";
import { PatientResourceLink } from "../../../types/file/ResourceLinkTypes";
import { PatientTreatmentPlan } from "../../../types/healthcare/TreatmentPlanTypes";
import { PatientOverviewSummary } from "../../../types/healthcare/PatientOverviewTypes";
import { countAbnormalResults } from "../../../utils/healthcare/testDisplayUtils";
import {
	applyOneNameOnlyToPrimaryNameDraft,
	formatPatientPrimaryDisplayName,
	isDuplicateUsernameFieldError,
	mergePatientPrimaryNameDraft,
	normalisePatientPrimaryNamePayload,
	patientCreateUsername,
	PatientPrimaryNameDraft,
	patientPrimaryNameDraftFromPatient,
	patientPrimaryNameDraftsMatch,
	PATIENT_USERNAME_TAKEN_MESSAGE,
	validatePatientPrimaryName,
} from "./patientNames/patientNameDisplay";
import { Company } from "../../../types/admin/CompanyTypes";
import { PatientNote } from "../../../types/healthcare/PatientNotes";

// Utility functions
import { generatePassword } from "../../../utils/generatePassword";
import { useNotifications } from "../../../context/NotificationContext";
import { useXoeFocusPublisher } from "../../../context/XoeFocusContext";
import { hasFeature } from "../../../utils/subscriptions/subscriptionService";
import { PatientPrimaryNameEditControls } from "./patientNames/PatientPrimaryNameSection";

// Tab components
import SupplementsTab from "./modalComponents/SupplementsTab";
import ProfileTab from "./modalComponents/ProfileTab";
import BillingTab from "./modalComponents/BillingTab";
import FilesTab from "./modalComponents/FilesTab";
import FormsTab from "./modalComponents/FormsTab";
import SentNotificationsTab from "./modalComponents/NotificationsTab";
import AppointmentTab from "./modalComponents/AppointmentTab";
import MedicalHistoryTab from "./modalComponents/MedicalHistoryTab";
import MedicationsTab from "./modalComponents/MedicationsTab";
import PrescriptionsTab from "./modalComponents/prescriptions/PrescriptionsTab";
import MedicalDashboard from "./modalComponents/MedicalDashboard";
import Notes from "./modalComponents/Notes";
import TestingTab from "./modalComponents/TestingTab";
import VitalsTab from "./modalComponents/VitalsTab";
import TreatmentPlanTab from "./modalComponents/TreatmentPlanTab";
import OrdersTab from "./modalComponents/OrdersTab";

// Modals
import ResetPasswordeModal from "./modalComponents/submodals/ResetPasswordModal";
import MedicalDirectorImportModal from "./MedicalDirectorImportModal";
import CustomerRecordDrawer from "./CustomerRecordDrawer";
import SampleTab from '../../../components/editUserModal/tabs/SampleTab';
import type { UserUuid } from "../../../types/identity";


const calculateAge = (dob: Date | string | null): number | null => {
	if (!dob) return null;
	const birthDate = new Date(dob as string);
	if (isNaN(birthDate.getTime())) return null;
	const today = new Date();
	let age = today.getFullYear() - birthDate.getFullYear();
	const m = today.getMonth() - birthDate.getMonth();
	if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) age--;
	return age;
};

// Server-owned IHI trust state, used to reconcile after an evaluated manual-IHI outcome.
interface ServerConfirmedIhiState {
	ihi_number?: number;
	ihi_status?: string;
	ihi_record_status?: string;
	ihi_source?: string;
}

const extractServerConfirmedIhiState = (record: Patient): ServerConfirmedIhiState => ({
	ihi_number: record.patient_info?.ihi_number,
	ihi_status: record.patient_info?.ihi_status,
	ihi_record_status: record.patient_info?.ihi_record_status,
	ihi_source: record.patient_info?.ihi_source,
});


// One primary-name edit session; `original` is the committed tuple captured on Edit, so Cancel can restore it without a request.
interface PrimaryNameEditSession {
	draft: PatientPrimaryNameDraft;
	original: PatientPrimaryNameDraft;
}

/** What a dirty primary-name draft is blocking, while the operator decides. */
type PendingPrimaryNameExit = "close" | "refresh" | "replace";

// Lifecycle of an autosave accepted while a primary-name commit held the write lane; a replacement waits until this is "idle".
type DeferredAutoSaveState = "idle" | "queued" | "running" | "failed";

/** Bounded, value-free: why a waiting record has not opened yet. */
const REPLACEMENT_HELD_MESSAGE = "The other patient record will open once this change has been saved.";

// Outcome of a primary-name commit; "blocked" (nothing sent) is distinct from "failed" so callers can retry.
type PrimaryNameCommitOutcome = "saved" | "failed" | "blocked";

/** Bounded, value-free: an explicit name Save arrived while another write was still running. */
const PRIMARY_NAME_SAVE_BUSY_TITLE = "Name not saved yet";
const PRIMARY_NAME_SAVE_BUSY_MESSAGE = "Another change is still saving. Try again in a moment.";

// Re-applies fields the operator changed since `baseline` over the server record, so an unrelated in-flight edit survives reconciliation while the committed name still wins.
const reapplyConcurrentEdits = (server: Patient, baseline: Patient, current: Patient): Patient => {
	if (baseline === current) return server;

	const merged = { ...server } as Record<string, unknown>;
	const baselineFields = baseline as unknown as Record<string, unknown>;
	const currentFields = current as unknown as Record<string, unknown>;
	Object.keys(currentFields).forEach((key) => {
		if (key === "patient_info") return;
		if (currentFields[key] !== baselineFields[key]) merged[key] = currentFields[key];
	});

	const baselineInfo = (baseline.patient_info ?? {}) as Record<string, unknown>;
	const currentInfo = (current.patient_info ?? {}) as Record<string, unknown>;
	const mergedInfo = { ...((server.patient_info ?? {}) as Record<string, unknown>) };
	Object.keys(currentInfo).forEach((key) => {
		if (currentInfo[key] !== baselineInfo[key]) mergedInfo[key] = currentInfo[key];
	});
	merged.patient_info = mergedInfo;

	return merged as unknown as Patient;
};

interface PatientModalProps {
	isOpen: boolean;
	onClose: () => void;
	patient: Patient;
	onPatientUpdated?: (patient: Patient) => void;
}

export interface PatientData {
	tests: Test[];
	medications: Medication[];
	medicalHistory: MedicalHistory[];
	appointments: Appointment[];
	completedAppointments: CompletedAppointment[];
	notifications: SentNotification[];
	forms: AssignedForm[];
	files: FileMetadata[];
	resourceLinks: PatientResourceLink[];
	reports: ReportFile[];
	treatmentPlans: PatientTreatmentPlan[];
}

const PatientModal: React.FC<PatientModalProps> = ({ isOpen, onClose, patient, onPatientUpdated }) => {
	const [updatedPatient, setUpdatedPatient] = useState<Patient>({ ...patient });
	const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
	const [errorMessages, setErrorMessages] = useState<string[]>([]);
	const [activeTab, setActiveTab] = useState("Overview");
	const [passwordModalOpen, setPasswordModalOpen] = useState<boolean>(false);
	const [importModalOpen, setImportModalOpen] = useState<boolean>(false);
	const [isExporting, setIsExporting] = useState<boolean>(false);
	const [showNotes, setShowNotes] = useState<boolean>(false);
	const [notes, setNotes] = useState<PatientNote[]>(() => {
		if (patient.patient_info?.notes && typeof patient.patient_info.notes === "string") {
			try {
				return JSON.parse(patient.patient_info.notes);
			} catch {
				return [];
			}
		}
		return [];
	});

	const [isLoadingData, setIsLoadingData] = useState<boolean>(false);
	const [isLoadingReports, setIsLoadingReports] = useState<boolean>(false);
	const [patientData, setPatientData] = useState<PatientData>({
		tests: [],
		medications: [],
		medicalHistory: [],
		appointments: [],
		completedAppointments: [],
		notifications: [],
		forms: [],
		files: [],
		resourceLinks: [],
		reports: [],
		treatmentPlans: [],
	});
	const [company, setCompany] = useState<Company>(null);
	const [overviewSummary, setOverviewSummary] = useState<PatientOverviewSummary | null>(null);
	const [analysisStatus, setAnalysisStatus] = useState<string | null>(null);

	const [selectedTemplate, setSelectedTemplate] = useState<Template | null>(null);
	const [showNotificationSelector, setShowNotificationSelector] = useState<boolean>(false);

	// Bumped only when a save returned an evaluated manual-IHI outcome; the Billing tab reconciles its raw field on the change.
	const [ihiEvaluationSignal, setIhiEvaluationSignal] = useState<number>(0);

	// Bumped after a successful save; the patient-name section re-reads the server projection on the change.
	const [nameSaveSignal, setNameSaveSignal] = useState<number>(0);

	// CU-14ygtkj98p4 (HI_015_006105): survives later unrelated saves; cleared only when a different patient becomes active.
	const [coreDemographicRevalidation, setCoreDemographicRevalidation] = useState<CoreDemographicRevalidationOffer | null>(null);

	// CU-14ygtkj98p8: keeps the revalidation result visible after it removed the verified IHI that normally hosts the panel.
	const [ihiRevalidationChangedRecord, setIhiRevalidationChangedRecord] = useState(false);
	// CU-14ygtkj98p8: after a server-side IHI change, lastServerConfirmedIhi is stale until a fresh read succeeds.
	const [ihiServerStateUnconfirmed, setIhiServerStateUnconfirmed] = useState(false);

	// Last server-confirmed IHI state; the 422 discard path restores this instead of blanking the field.
	const lastServerConfirmedIhi = useRef<ServerConfirmedIhiState>(extractServerConfirmedIhiState(patient));

	// Held OUTSIDE updatedPatient so autosave always carries the last committed name tuple and a refresh can't overwrite an active draft.
	const [primaryNameSession, setPrimaryNameSession] = useState<PrimaryNameEditSession | null>(null);
	const primaryNameSessionRef = useRef<PrimaryNameEditSession | null>(null);
	const [pendingPrimaryNameExit, setPendingPrimaryNameExit] = useState<PendingPrimaryNameExit | null>(null);

	// The record this modal actually operates on; using it over the prop stops a deferred replacement sending the outgoing draft to the incoming id.
	const [activePatient, setActivePatient] = useState<Patient>(patient);
	// Incoming record held until the outgoing draft resolves; held explicitly because the outgoing save echoes back through onPatientUpdated.
	const [pendingPatient, setPendingPatientState] = useState<Patient | null>(null);
	// Mirrored so a write's async completion reads whether a replacement is waiting now, not at the render that queued it.
	const pendingPatientRef = useRef<Patient | null>(null);
	const setPendingPatient = (next: Patient | null) => {
		pendingPatientRef.current = next;
		setPendingPatientState(next);
	};

	// The ref is the authority against overlapping commits — two rapid clicks both run before React re-renders, so a state flag can't stop the second.
	const primaryNameCommitRef = useRef(false);
	const [isCommittingPrimaryName, setIsCommittingPrimaryName] = useState(false);
	/** An autosave that landed mid-commit: deferred until it resolves, never dropped. */
	const [deferredAutoSave, setDeferredAutoSave] = useState<DeferredAutoSaveState>("idle");
	const deferredAutoSaveRef = useRef<DeferredAutoSaveState>("idle");
	const setDeferredAutoSaveState = (next: DeferredAutoSaveState) => {
		deferredAutoSaveRef.current = next;
		setDeferredAutoSave(next);
	};
	// A COUNT of in-flight writes, not a flag: a boolean would let an older write's finally clear a newer write's state.
	const activeWriteCountRef = useRef(0);
	const [activeWriteCount, setActiveWriteCount] = useState(0);
	const activeWriteOwnerRef = useRef<UserUuid | null>(null);
	const beginActiveWrite = (ownerId: UserUuid) => {
		if (activeWriteCountRef.current === 0) activeWriteOwnerRef.current = ownerId;
		activeWriteCountRef.current += 1;
		setActiveWriteCount(activeWriteCountRef.current);
	};
	const endActiveWrite = () => {
		activeWriteCountRef.current = Math.max(0, activeWriteCountRef.current - 1);
		if (activeWriteCountRef.current === 0) activeWriteOwnerRef.current = null;
		setActiveWriteCount(activeWriteCountRef.current);
	};

	/** Committed state at commit start, so reconciliation can tell what the operator changed since. */
	const concurrentEditBaselineRef = useRef<Patient | null>(null);
	const isMountedRef = useRef(true);
	useEffect(() => () => { isMountedRef.current = false; }, []);

	const applyPrimaryNameSession = (next: PrimaryNameEditSession | null) => {
		primaryNameSessionRef.current = next;
		setPrimaryNameSession(next);
	};

	const isPrimaryNameDraftDirty =
		!!primaryNameSession && !patientPrimaryNameDraftsMatch(primaryNameSession.draft, primaryNameSession.original);

	const { showError, showSuccess, showWarning, showInfo } = useNotifications();

	// The active record still owns a write, so a replacement waits without a prompt — unlike a dirty draft, which is the operator's to resolve.
	const hasUnresolvedActiveWrite =
		isCommittingPrimaryName || deferredAutoSave !== "idle" || activeWriteCount > 0;

	// A different record only becomes active once the outgoing one has no unresolved write and no dirty draft; a refreshed object for the same patient is adopted at once.
	useEffect(() => {
		if (pendingPatient) {
			if (hasUnresolvedActiveWrite || isPrimaryNameDraftDirty) {
				// Follow a further replacement request, but never re-hold the outgoing record its own save just echoed back.
				if (patient !== pendingPatient && patient?.id !== activePatient?.id) setPendingPatient(patient);
				if (isPrimaryNameDraftDirty && !hasUnresolvedActiveWrite) setPendingPrimaryNameExit("replace");
				return;
			}
			applyPrimaryNameSession(null);
			setPendingPrimaryNameExit(null);
			setActivePatient(pendingPatient);
			setPendingPatient(null);
			return;
		}
		if (patient === activePatient) return;
		if (patient?.id !== activePatient?.id) {
			if (hasUnresolvedActiveWrite || isPrimaryNameDraftDirty) {
				setPendingPatient(patient);
				if (isPrimaryNameDraftDirty && !hasUnresolvedActiveWrite) setPendingPrimaryNameExit("replace");
				return;
			}
			applyPrimaryNameSession(null);
			setPendingPrimaryNameExit(null);
		}
		setActivePatient(patient);
	}, [patient, activePatient, pendingPatient, isPrimaryNameDraftDirty, hasUnresolvedActiveWrite]);

	useEffect(() => {
		const init = async () => {
			lastServerConfirmedIhi.current = extractServerConfirmedIhiState(activePatient);
			setUpdatedPatient({
				...activePatient,
				patient_info: activePatient.patient_info ?? {},
			});
			setNotes(() => {
				if (activePatient.patient_info?.notes && typeof activePatient.patient_info.notes === "string") {
					try {
						return JSON.parse(activePatient.patient_info.notes);
					} catch {
						return [];
					}
				}
				return [];
			});
			setErrorMessages([]);

			if (activePatient.id) {
				await loadAllPatientData();
			} else {
				setActiveTab("Personal");
			}

			let comp = await fetchClinicCompany();
			setCompany(comp);
		};
		init();
	}, [activePatient]);

	useEffect(() => {
		setUpdatedPatient((prev) => ({
			...prev,
			patient_info: { ...prev.patient_info, notes: JSON.stringify(notes) },
		}));
	}, [notes]);

	useEffect(() => {
		setCoreDemographicRevalidation(null);
	}, [activePatient?.id]);

	useEffect(() => {
		setCoreDemographicRevalidation(null);
	}, [activePatient?.id]);

	useEffect(() => {
		setIhiRevalidationChangedRecord(false);
		setIhiServerStateUnconfirmed(false);
	}, [activePatient?.id]);

	// Publish the focused patient onto the Xoe focus bus. This modal is the single publisher since `activeTab` is private local state no parent can supply.
	const { setPatientFocus, clearPatientFocus } = useXoeFocusPublisher();
	const focusPatientId = activePatient?.id;
	const focusPatientLabel = formatPatientPrimaryDisplayName(updatedPatient);
	useEffect(() => {
		if (!isOpen || !focusPatientId) return;
		setPatientFocus({
			patientId: focusPatientId,
			patientLabel: focusPatientLabel,
			// A tab KEY, not a label — keys and labels genuinely disagree ("Files" reads "Resources"), so the bus resolves it.
			sectionKey: activeTab,
		});
		// Cleared only while this instance is still the focused record, so a second closed modal can't wipe a live focus.
		return () => clearPatientFocus(focusPatientId);
	}, [isOpen, focusPatientId, activeTab, focusPatientLabel, setPatientFocus, clearPatientFocus]);

	const loadAllPatientData = async () => {
		if (!activePatient.id) return;
		setIsLoadingData(true);
		try {
			// Phase 1: aggregated overview plus cross-service data, all parallel.
			const [
				overview, notifications, forms, files, resourceLinks,
			] = await Promise.all([
				fetchPatientOverview(activePatient.id).catch(() => null),
				fetchSentNotifications(activePatient.id ? { recipient_user_id: activePatient.id } : { recipient_email: activePatient.email }).catch(() => []),
				getAssignedForms(activePatient.id).catch(() => []),
				listUserFiles(activePatient.id).catch(() => []),
				getPatientResourceLinks(activePatient.id).catch(() => []),
			]);

			const tests = overview?.tests ?? [];
			const medications = overview?.medications ?? [];
			const medHist = overview?.medical_history ?? [];
			const appointments = overview?.appointments ?? [];
			const treatmentPlans = (overview?.treatment_plans ?? []).filter((p) => p.template?.is_peptide_plan);

			setOverviewSummary(overview?.summary ?? null);
			setAnalysisStatus(overview?.analysis?.status ?? null);

			setPatientData({ tests, medications, medicalHistory: medHist, appointments, completedAppointments: [], notifications, forms, files, resourceLinks, reports: [], treatmentPlans });
			setUpdatedPatient((prev) => ({ ...prev, medical_history: medHist, tests, medications, appointments }));
			setIsLoadingData(false);

			// Phase 2: reports, fills Test Reports card only.
			setIsLoadingReports(true);
			try {
				const reportArrays = await Promise.all(
					tests
						.filter((t) => t.sample_id)
						.map((t) => listPatientReports(activePatient.id, t.sample_id!).catch(() => []))
				);
				const allReports = reportArrays.flat().filter((r) => !r.clinical_only);
				const unique = allReports.filter((r, i, self) => i === self.findIndex((x) => x.id === r.id));
				setPatientData((prev) => ({ ...prev, reports: unique }));
			} catch {
				// Phase 2 failure is non-fatal — reports card shows empty state
			} finally {
				setIsLoadingReports(false);
			}

		} catch (error) {
			console.error("Error loading patient data:", error);
			showError("Failed to load some patient data");
			setIsLoadingData(false);
		}
	};

	const loadFreshPatientProfile = async () => {
		if (!activePatient.id) return null;

		const { data: freshPatient } = await fetchPatient(String(activePatient.id));
		const normalisedPatient = {
			...freshPatient,
			patient_info: freshPatient.patient_info ?? {},
		};
		lastServerConfirmedIhi.current = extractServerConfirmedIhiState(normalisedPatient);
		setIhiServerStateUnconfirmed(false);
		// A plain replace would erase an unrelated in-flight edit; only a primary-name commit sets a baseline, every other caller keeps replace semantics.
		setUpdatedPatient((prev) => {
			const baseline = concurrentEditBaselineRef.current;
			return baseline ? reapplyConcurrentEdits(normalisedPatient, baseline, prev) : normalisedPatient;
		});
		onPatientUpdated?.(normalisedPatient);

		return normalisedPatient;
	};

	// CU-14ygtkj98p8 (HI_015_016813): the server replaced or cleared the current IHI, so reconcile before presenting it again.
	const handleIhiRevalidationRecordChanged = async () => {
		setIhiRevalidationChangedRecord(true);
		setIhiServerStateUnconfirmed(true);
		// CU-14ygtkj98p8: drop the stale IHI so no autosave or 422 restore can resubmit it before a fresh read succeeds.
		lastServerConfirmedIhi.current = {};
		setUpdatedPatient((prev) => ({
			...prev,
			patient_info: { ...prev.patient_info, ihi_number: undefined, ihi_status: undefined, ihi_record_status: undefined, ihi_source: undefined },
		}));
		setIhiEvaluationSignal((n) => n + 1);
		try {
			await loadFreshPatientProfile();
			setIhiEvaluationSignal((n) => n + 1);
		} catch (error) {
			console.error("Error refreshing patient profile:", error);
			showError("Failed to refresh patient profile.");
		}
	};

	const handleRefreshData = async () => {
		try {
			await loadFreshPatientProfile();
			await loadAllPatientData();
			showSuccess("Data refreshed successfully");
		} catch (error) {
			console.error("Error refreshing patient profile:", error);
			showError("Failed to refresh patient profile.");
		}
	};

	const onActiveClick = async () => {
		setIsSubmitting(true);
		try {
			const updated = { ...updatedPatient };
			if (!updatedPatient.is_active) {
				await activatePatient(activePatient.id);
				updated.is_active = true;
			} else {
				await deactivatePatient(activePatient.id);
				updated.is_active = false;
			}
			setUpdatedPatient(updated);
		} catch (error) {
			handleErrors(error);
		} finally {
			setIsSubmitting(false);
		}
	};

	// The canonical patient write. Only an explicit save (commitPrimaryName) folds the name draft in; every other write carries the last committed tuple.
	const updatePatientInfo = async (
		isSendNotification: boolean,
		{ commitPrimaryName = false }: { commitPrimaryName?: boolean } = {}
	): Promise<boolean> => {
		const committingDraft = commitPrimaryName ? primaryNameSession?.draft ?? null : null;
		// Owns a write until the whole function — request and the reconciliation below — has settled.
		beginActiveWrite(activePatient.id);
		setIsSubmitting(true);
		try {
			// Normalise the canonical primary name at the final save boundary, on a dedicated payload so updatedPatient is never mutated and validation runs against what is actually sent.
			const savePayload = normalisePatientPrimaryNamePayload(
				mergePatientPrimaryNameDraft(updatedPatient, committingDraft)
			);

			const primaryNameErrors = validatePatientPrimaryName(savePayload);
			if (primaryNameErrors.length > 0) {
				setErrorMessages(primaryNameErrors);
				setIsSubmitting(false);
				return false;
			}
			// The response may carry an ihi_validation block; parsed fail-closed to null and never stored as Patient state or logged.
			let ihiValidation: IhiValidationFeedback | null = null;
			let revalidationOffer: CoreDemographicRevalidationOffer | null = null;
			if (activePatient.id !== "") {
				const response = await updatePatient(activePatient.id, savePayload);
				ihiValidation = parseIhiValidationFeedback(response?.ihi_validation);
				revalidationOffer = parseCoreDemographicRevalidationOffer(response?.core_demographic_ihi_revalidation);
				if (revalidationOffer) {
					const offer = revalidationOffer;
					setCoreDemographicRevalidation((current) => mergeCoreDemographicRevalidationOffers(current, offer));
				}
				await loadFreshPatientProfile();
			} else {
				// Bare `first_last` slug from the normalised name components; ProfileTab previews the same helper so preview and submit can't drift.
				const createUsername = patientCreateUsername(savePayload);
				// Randomly generated, never the name slug: a name-derived password was guessable and a short slug failed the auth service's min:8 rule.
				const generatedPassword = generatePassword();
				// Separate payload so the patient state object never carries credential or user-type fields.
				const createPayload: Patient = {
					...savePayload,
					username: createUsername,
					password: generatedPassword,
					password_confirmation: generatedPassword,
					user_type: "patient",
				};
				const { ihi_validation, ...newPatient } = await createPatient(createPayload);
				ihiValidation = parseIhiValidationFeedback(ihi_validation);
				lastServerConfirmedIhi.current = extractServerConfirmedIhiState(newPatient);
				setUpdatedPatient(newPatient);
				onPatientUpdated?.(newPatient);
				if (isSendNotification) {
					const notificationPayload: SendCommunicationRequest = {
						recipient_user_id: newPatient.id,
						category: "new_patient_notification",
						id: selectedTemplate?.id,
						user_id: getSessionUser().id,
						channels: ['email', 'sms'],
						// Password from the sent payload, not the response (the User model hides it); this generated credential exists only here.
						data: { ...newPatient, password: generatedPassword },
					};
					if (ENV.VITE_NOTIFICATIONS_ENABLED === "true") {
						sendCommunication(notificationPayload);
					}
				}
			}
			// Save succeeded: re-read the structured-name projection.
			setNameSaveSignal((n) => n + 1);
			// Draft is now the server-confirmed tuple, so close the session; a failed save never reaches here.
			if (committingDraft) applyPrimaryNameSession(null);
			if (ihiValidation) {
				// Reconcile the Billing-tab raw field to the server-confirmed state now in updatedPatient.
				setIhiEvaluationSignal((n) => n + 1);
				if (ihiValidation.candidate_discarded) {
					showWarning("IHI not saved", ihiValidation.message);
					showSuccess("Patient saved successfully.");
				} else {
					showSuccess("Patient saved successfully.", ihiValidation.message);
				}
			} else {
				showSuccess("Patient saved successfully.");
			}
			if (revalidationOffer) {
				showInfo(
					"IHI revalidation available",
					`${describeChangedDemographics(revalidationOffer)} changed. Revalidation is not automatic; request it from the Personal Info tab.`
				);
			}
			return true;
		} catch (error) {
			// A mandatory-IHI 422 may carry a not-saved ihi_validation block; reconcile the discarded candidate before ordinary error handling.
			const rejectedIhiValidation = extractIhiValidationFromMutationError(error);
			if (rejectedIhiValidation?.candidate_discarded) {
				// The failed mutation left the server unchanged, so restore the last confirmed IHI state; only IHI fields are touched, other edits are kept.
				const confirmedIhi = lastServerConfirmedIhi.current;
				setUpdatedPatient((prev) => ({
					...prev,
					patient_info: {
						...prev.patient_info,
						ihi_number: confirmedIhi.ihi_number,
						ihi_status: confirmedIhi.ihi_status,
						ihi_record_status: confirmedIhi.ihi_record_status,
						ihi_source: confirmedIhi.ihi_source,
					},
				}));
				setIhiEvaluationSignal((n) => n + 1);
				showWarning("Patient not saved", rejectedIhiValidation.message);
			}
			handleErrors(error);
			return false;
		} finally {
			setIsSubmitting(false);
			endActiveWrite();
		}
	};

	// One debounced autosave for the whole modal, reading the current save closure from a ref so rapid edits coalesce into a single request.
	const autoSaveActionRef = useRef<() => void>(() => {});

	useEffect(() => {
		autoSaveActionRef.current = () => {
			if (!activePatient.id) return;
			// Never overlap the commit: this request carries the previous tuple and would restore the old name, so it's deferred and replayed, never dropped.
			if (primaryNameCommitRef.current) {
				setDeferredAutoSaveState("queued");
				return;
			}
			// Track a deferred edit or retry to completion so a held replacement knows when this record has no unresolved write left.
			const owesDeferredEdit = deferredAutoSaveRef.current !== "idle";
			if (owesDeferredEdit) setDeferredAutoSaveState("running");

			void updatePatientInfo(false).then((saved) => {
				if (saved) {
					if (owesDeferredEdit) setDeferredAutoSaveState("idle");
					return;
				}
				// A permitted edit did not persist; if a replacement is waiting, hold it so the edit isn't dropped silently.
				if (!owesDeferredEdit && !pendingPatientRef.current) return;
				setDeferredAutoSaveState("failed");
				if (pendingPatientRef.current) {
					setErrorMessages((prev) =>
						prev.includes(REPLACEMENT_HELD_MESSAGE) ? prev : [...prev, REPLACEMENT_HELD_MESSAGE]
					);
				}
			});
		};
	});

	const handleAutoSave = useMemo(() => debounce(() => autoSaveActionRef.current(), 500), []);

	useEffect(() => {
		if (!isOpen) handleAutoSave.cancel();
	}, [isOpen, handleAutoSave]);

	useEffect(() => {
		handleAutoSave.cancel();
	}, [activePatient, handleAutoSave]);

	useEffect(() => () => handleAutoSave.cancel(), [handleAutoSave]);

	const handleErrors = (error: unknown) => {
		if (axios.isAxiosError(error) && error.response) {
			if (error.response.data?.errors) {
				const messages = Object.entries(error.response.data.errors).flatMap(([field, errs]) =>
					isDuplicateUsernameFieldError(field, errs as string[]) ? [PATIENT_USERNAME_TAKEN_MESSAGE] : (errs as string[])
				);
				setErrorMessages(messages);
			} else if (error.response.data?.message) {
				setErrorMessages([error.response.data.message]);
			} else {
				setErrorMessages(["An unknown error occurred."]);
			}
		} else if (error instanceof Error) {
			setErrorMessages([error.message || "An unknown error occurred."]);
		} else {
			setErrorMessages(["An unknown error occurred."]);
		}
	};

	// Replay a deferred autosave through the one stable debounce so it inherits every guard and re-reads reconciled state at fire time.
	const flushDeferredAutoSave = () => {
		if (deferredAutoSaveRef.current !== "queued") return;
		if (!isMountedRef.current) {
			setDeferredAutoSaveState("idle");
			return;
		}
		handleAutoSave();
	};

	// The single canonical primary-name commit, reached by both Save buttons; supersedes any queued autosave that would resubmit an older snapshot.
	const commitPatientWithPrimaryName = async (): Promise<PrimaryNameCommitOutcome> => {
		// Synchronous guard: a rapid double-click runs both handlers before the button's loading state applies.
		if (primaryNameCommitRef.current) return "blocked";
		// Inverse race: an ordinary write still carries the previous tuple, so refuse a click in the render gap rather than race two writes.
		if (activeWriteCountRef.current > 0) {
			showWarning(PRIMARY_NAME_SAVE_BUSY_TITLE, PRIMARY_NAME_SAVE_BUSY_MESSAGE);
			return "blocked";
		}
		primaryNameCommitRef.current = true;
		setIsCommittingPrimaryName(true);
		concurrentEditBaselineRef.current = updatedPatient;
		handleAutoSave.cancel();
		try {
			const committed = await updatePatientInfo(false, { commitPrimaryName: true });
			// An explicit Save writes the whole record, so it settles an edit whose deferred autosave had failed.
			if (committed && deferredAutoSaveRef.current === "failed") setDeferredAutoSaveState("idle");
			return committed ? "saved" : "failed";
		} finally {
			primaryNameCommitRef.current = false;
			concurrentEditBaselineRef.current = null;
			setIsCommittingPrimaryName(false);
			flushDeferredAutoSave();
		}
	};

	const handleSaveClick = () => {
		if (updatedPatient.id === "") {
			handleAutoSave.cancel();
			sendNotificationCheck();
		} else {
			void commitPatientWithPrimaryName();
		}
	};

	const primaryNameEdit: PatientPrimaryNameEditControls = {
		draft: primaryNameSession?.draft ?? null,
		// Commit-specific flag, not isSubmitting, which every autosave sets and would freeze the name group.
		isSaving: isCommittingPrimaryName,
		// Another write is unresolved: the Save action waits, the draft doesn't.
		isBlocked: activeWriteCount > 0 && !isCommittingPrimaryName,
		onEdit: () => {
			const committed = patientPrimaryNameDraftFromPatient(updatedPatient);
			applyPrimaryNameSession({ draft: committed, original: committed });
		},
		onChange: (patch) => {
			const current = primaryNameSessionRef.current;
			// The submitted tuple was snapshotted at request start, so a change accepted now would be discarded by the success path.
			if (!current || primaryNameCommitRef.current) return;
			applyPrimaryNameSession({ ...current, draft: { ...current.draft, ...patch } });
		},
		onOneNameOnlyChange: (checked) => {
			const current = primaryNameSessionRef.current;
			if (!current || primaryNameCommitRef.current) return;
			applyPrimaryNameSession({
				...current,
				draft: applyOneNameOnlyToPrimaryNameDraft(current.draft, checked, current.original),
			});
		},
		onCancel: () => applyPrimaryNameSession(null),
		onSave: () => {
			void commitPatientWithPrimaryName();
		},
	};

	// An action that would discard a dirty name draft asks first; it never submits the draft on its own.
	const requestClose = () => {
		if (isPrimaryNameDraftDirty) {
			setPendingPrimaryNameExit("close");
			return;
		}
		applyPrimaryNameSession(null);
		onClose();
	};

	const requestRefresh = () => {
		if (isPrimaryNameDraftDirty) {
			setPendingPrimaryNameExit("refresh");
			return;
		}
		applyPrimaryNameSession(null);
		void handleRefreshData();
	};

	// "replace" needs no completion step — clearing the draft lets the held record activate through the replacement effect.
	const completePrimaryNameExit = (exit: PendingPrimaryNameExit) => {
		setPendingPrimaryNameExit(null);
		if (exit === "close") {
			onClose();
		} else if (exit === "refresh") {
			void handleRefreshData();
		}
	};

	const handleUnsavedPrimaryNameSave = async () => {
		const exit = pendingPrimaryNameExit;
		// A second click while the first commit is running must not be read as a failed save.
		if (!exit || primaryNameCommitRef.current) return;
		const outcome = await commitPatientWithPrimaryName();
		// "blocked" sent nothing: keep the boundary open so the operator can retry once the other write settles.
		if (outcome === "blocked") return;
		if (outcome === "failed") {
			// The outgoing record stays active with its draft, so the operator can correct and retry.
			setPendingPrimaryNameExit(null);
			return;
		}
		completePrimaryNameExit(exit);
	};

	const handleUnsavedPrimaryNameDiscard = () => {
		const exit = pendingPrimaryNameExit;
		applyPrimaryNameSession(null);
		if (exit) completePrimaryNameExit(exit);
	};

	const sendNotificationCheck = () => {
		Modal.confirm({
			title: "Send Notification?",
			content: "Would you like to send an email to the new patient?",
			icon: null,
			okText: "Yes",
			cancelText: "No",
			onOk: () => setShowNotificationSelector(true),
			onCancel: () => updatePatientInfo(false),
		});
	};

	// Notification Selector Dialog
	const NotificationSelectorDialog = () => {
		const [localTemplates, setLocalTemplates] = useState<Template[]>([]);
		useEffect(() => {
			const load = async () => {
				try {
					const { data } = await fetchTemplates("new_patient_notification");
					setLocalTemplates(data);
				} catch { }
			};
			load();
		}, []);
		const options = localTemplates.map((t) => ({
			label: `${t.name} ${t.is_active ? "(Default)" : ""}`,
			value: t.id,
		}));
		return (
			<Modal title="Select the template to send" open={showNotificationSelector} onCancel={() => setShowNotificationSelector(false)} footer={null}>
				<Select
					style={{ width: "100%" }}
					placeholder="Select template"
					options={options}
					onChange={(value) => {
						const selected = localTemplates.find((t) => t.id === value);
						if (selected) {
							setSelectedTemplate(selected);
							setShowNotificationSelector(false);
							updatePatientInfo(true);
						}
					}}
				/>
			</Modal>
		);
	};

	const isPeptideOnly = hasFeature("healthcare.peptide") && !hasFeature("healthcare");

	const patientInfoStrip = updatedPatient.id ? (() => {
		const now = new Date();

		// Next upcoming appointment
		const nextAppt = patientData.appointments
			.filter((a) => (fromUtc(a.start_time)?.valueOf() ?? 0) > now.getTime())
			.sort((a, b) => (fromUtc(a.start_time)?.valueOf() ?? 0) - (fromUtc(b.start_time)?.valueOf() ?? 0))[0] ?? null;

		interface InfoCol {
			key: string;
			label: string;
			value: React.ReactNode;
			grow?: number;
			wrap?: boolean;
		}
		const cols: InfoCol[] = [];

		// Built from recorded components only, so a one-name-only record shows its single name once.
		cols.push({
			key: "name",
			label: "Patient",
			value: formatPatientPrimaryDisplayName(updatedPatient),
			grow: 1,
		});

		// Contact — email + phone stacked in one column
		const emailVal = updatedPatient.email?.trim();
		const phoneVal = updatedPatient.phone?.trim();
		if (emailVal || phoneVal) {
			cols.push({
				key: "contact",
				label: "Contact",
				wrap: true,
				value: (
					<span style={{ display: "flex", flexDirection: "column", gap: 1 }}>
						{emailVal && <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{emailVal}</span>}
						{phoneVal && <span>{phoneVal}</span>}
					</span>
				),
				grow: 1,
			});
		}

		// Age + DOB
		const age = calculateAge(updatedPatient.date_of_birth);
		const dobStr = updatedPatient.date_of_birth
			? new Date(updatedPatient.date_of_birth as unknown as string).toLocaleDateString("en-AU", { day: "2-digit", month: "2-digit", year: "numeric" })
			: null;
		if (age !== null && dobStr) cols.push({ key: "age", label: "Age / DOB", value: `${age}y (${dobStr})`, grow: 1 });
		else if (dobStr) cols.push({ key: "dob", label: "DOB", value: dobStr });

		// Sex / Gender
		const sexVal = updatedPatient.sex?.trim();
		const genderVal = updatedPatient.gender?.trim();
		if (sexVal && genderVal && sexVal.toLowerCase() !== genderVal.toLowerCase()) {
			cols.push({ key: "sex", label: "Sex / Gender", value: `${sexVal} / ${genderVal}` });
		} else if (sexVal || genderVal) {
			cols.push({ key: "sex", label: "Sex / Gender", value: sexVal || genderVal });
		}

		// Height / Weight, metric (AU market); only append a unit to a bare number so we don't double up on "175 cm".
		const withUnit = (v: string, unit: string) => (/[a-zA-Z]/.test(v) ? v : `${v} ${unit}`);
		const ht = updatedPatient.height?.trim();
		const wt = updatedPatient.weight?.trim();
		if (ht && wt) cols.push({ key: "htwt", label: "Ht / Wt", value: `${withUnit(ht, "cm")} / ${withUnit(wt, "kg")}` });
		else if (ht) cols.push({ key: "height", label: "Height", value: withUnit(ht, "cm") });
		else if (wt) cols.push({ key: "weight", label: "Weight", value: withUnit(wt, "kg") });

		// Medicare
		if (updatedPatient.patient_info?.medicare_number) {
			const mcRef = updatedPatient.patient_info.medicare_individual_ref_number;
			cols.push({ key: "medicare", label: "Medicare", value: mcRef ? `${updatedPatient.patient_info.medicare_number}/${mcRef}` : String(updatedPatient.patient_info.medicare_number) });
		}

		// Blood group (imported from an external practice management system).
		if (updatedPatient.patient_info?.blood_group) {
			cols.push({ key: "blood_group", label: "Blood Group", value: updatedPatient.patient_info.blood_group });
		}

		// An active assertion of no allergies, not an empty (merely unknown) list; shown only when actually asserted.
		if (updatedPatient.patient_info?.no_known_allergies === true) {
			cols.push({ key: "nka", label: "Allergies", value: "No known allergies" });
		}

		// Imported Medical Director warning banner; can caveat the whole record's integrity, so never drop it silently.
		if (updatedPatient.patient_info?.clinical_warnings) {
			cols.push({
				key: "clinical_warning",
				label: "Warning",
				wrap: true,
				grow: 1,
				value: (
					<span
						title={updatedPatient.patient_info.clinical_warnings}
						style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "0 6px", borderRadius: 3, fontSize: 10, fontWeight: 600, background: "var(--mg-danger-bg)", border: "1px solid var(--mg-danger)", color: "var(--mg-danger)" }}
					>
						{updatedPatient.patient_info.clinical_warnings}
					</span>
				),
			});
		}

		// Active medications count
		if (overviewSummary && overviewSummary.active_medications_count > 0) {
			cols.push({ key: "active_meds", label: "Active Meds", value: String(overviewSummary.active_medications_count) });
		}

		// Critical / abnormal test results flag
		if (overviewSummary?.has_abnormal_results) {
			const abnormalResultsCount = patientData.tests.reduce((sum, t) => sum + countAbnormalResults(t), 0);
			cols.push({
				key: "alerts",
				label: "Alerts",
				value: (
					<span
						role="button"
						onClick={() => setActiveTab("Testing")}
						className="mg-alerts-chip-clickable"
						style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "0 6px", borderRadius: 3, fontSize: 10, fontWeight: 600, background: "var(--mg-danger-bg)", border: "1px solid var(--mg-danger)", color: "var(--mg-danger)", cursor: "pointer" }}
					>
						Outside reference range
						<Badge count={abnormalResultsCount} style={{ backgroundColor: "var(--mg-danger)" }} />
					</span>
				),
			});
		}

		// Active treatment plan
		if (overviewSummary?.active_treatment_plan?.name) {
			const plan = overviewSummary.active_treatment_plan;
			cols.push({
				key: "treatment_plan",
				label: "Treatment Plan",
				value: plan.name,
				grow: 1,
			});
		}

		// Next appointment
		if (nextAppt?.start_time) {
			// The viewer's own zone — a UTC read renders the previous DAY for a morning appointment.
			const apptDateStr = formatInZone(nextAppt.start_time, "DD MMM YYYY");
			const apptLabel = nextAppt.appointment_type?.name || nextAppt.title || "Appointment";
			cols.push({ key: "next_appt", label: "Next Appointment", value: `${apptDateStr} — ${apptLabel}`, grow: 1 });
		}

		if (cols.length === 0) return undefined;

		return (
			<div style={{ display: "flex", alignItems: "stretch", width: "100%", paddingTop: 6 }}>
				{cols.map((col, i) => (
					<React.Fragment key={col.key}>
						{i > 0 && (
							<div style={{ width: 1, background: "var(--mg-border, rgba(0,0,0,0.08))", margin: "0 10px", flexShrink: 0 }} />
						)}
						<div style={{ display: "flex", flexDirection: "column", minWidth: 0 }}>
							<span style={{ fontSize: 9, textTransform: "uppercase", letterSpacing: "0.6px", color: "var(--mg-text-secondary)", lineHeight: 1, marginBottom: 3, display: "block", whiteSpace: "nowrap" }}>
								{col.label}
							</span>
							<span style={{ fontSize: 11, lineHeight: 1.3, overflow: "hidden", textOverflow: col.wrap ? undefined : "ellipsis", whiteSpace: col.wrap ? undefined : "nowrap", color: "var(--mg-text)", fontWeight: 500 }}>
								{col.value}
							</span>
						</div>
					</React.Fragment>
				))}
			</div>
		);
	})() : undefined;

	const serverConfirmsCurrentIhi = hasServerConfirmedCurrentIhi(lastServerConfirmedIhi.current);
	// CU-14ygtkjb8x9 (HI_015_005875): only a re-read active patient record known to hold no IHI may search by address to have one assigned.
	const serverConfirmsNoCurrentIhi = !ihiServerStateUnconfirmed && !serverConfirmsCurrentIhi && updatedPatient.user_type === "patient" && updatedPatient.is_active === true;

	const tabItems = [
		{
			key: "Overview",
			label: "Overview",
			// S10 (14ygtkj83tv): "Xoe remembers" sits above the dashboard, on the existing tab — no new tab key.
			children: (
				<div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
					<MedicalDashboard patient={updatedPatient} patientData={patientData} onRefresh={loadAllPatientData} isLoading={isLoadingData} isLoadingReports={isLoadingReports} onNavigateToTab={setActiveTab} company={company} analysisStatus={analysisStatus} />
				</div>
			),
			disabled: !activePatient.id,
		},
		{
			key: "Personal",
			label: "Personal Info",
			// CU-14ygtkj98p4 (HI_015_006105): Personal Info is visible in every clinic mode, unlike Billing.
			children: (
				<>
					{activePatient.id && (serverConfirmsCurrentIhi || serverConfirmsNoCurrentIhi || ihiRevalidationChangedRecord) ? (
						<IhiRevalidationPanel
							key={activePatient.id}
							patientId={activePatient.id}
							offer={coreDemographicRevalidation}
							requestAvailable={!ihiServerStateUnconfirmed && hasServerConfirmedVerifiedIhi(lastServerConfirmedIhi.current)}
							addressSearchAvailable={!ihiServerStateUnconfirmed && (serverConfirmsCurrentIhi || serverConfirmsNoCurrentIhi)}
							ihiStateUnconfirmed={ihiServerStateUnconfirmed}
							noCurrentIhi={serverConfirmsNoCurrentIhi}
							onPatientRecordChanged={handleIhiRevalidationRecordChanged}
						/>
					) : null}
					<ProfileTab updatedPatient={updatedPatient} setUpdatedPatient={setUpdatedPatient} handleAutoSave={handleAutoSave} isAnon={updatedPatient.is_anonymous as boolean} showPatientNames nameSaveSignal={nameSaveSignal} primaryNameEdit={primaryNameEdit} />
				</>
			),
		},
		{
			key: "Billing",
			label: "Billing",
			// CU-14ygtkj8z2w: every lastServerConfirmedIhi update is followed by a setUpdatedPatient, so this render-time read is current.
			children: <BillingTab updatedPatient={updatedPatient} setUpdatedPatient={setUpdatedPatient} handleAutoSave={handleAutoSave} ihiEvaluationSignal={ihiEvaluationSignal} serverConfirmedIhi={lastServerConfirmedIhi.current} ihiStateUnconfirmed={ihiServerStateUnconfirmed} />,
			hidden: isPeptideOnly,
		},
		{
			key: "__divider_1__",
			label: "",
			children: null,
		},
		{
			key: "Appointments",
			label: "Appointments",
			children: <AppointmentTab patient={updatedPatient} />,
			hidden: isPeptideOnly,
		},
		{
			key: "Forms",
			label: "Forms",
			children: <FormsTab updatedPatient={updatedPatient} setUpdatedPatient={setUpdatedPatient} handleAutoSave={handleAutoSave} />,
		},
		{
			key: "Gene-Codes-Assignment",
			label: "Gene.Codes Assignment",
			children: <SampleTab patient={updatedPatient} isSubmitting={isSubmitting} setIsSubmitting={setIsSubmitting} setErrorMessages={setErrorMessages}  handleAutoSave={handleAutoSave} />,
			hidden: !(canUseOperatorTools() && can("labiq.sample_access.manage")),
		},
		{
			key: "Medical History",
			label: "Medical History",
			children: <MedicalHistoryTab patient={updatedPatient} />,
		},
		{
			key: "Medications",
			label: "Medications",
			children: <MedicationsTab patient={updatedPatient} handleAutoSave={handleAutoSave} />,
		},
		{
			key: "Prescriptions",
			label: "Prescriptions",
			children: <PrescriptionsTab patient={updatedPatient} />,
			disabled: !activePatient.id,
			hidden: !canViewPrescriptionDrafts(),
		},
		{
			key: "Notifications",
			label: "Notifications",
			children: <SentNotificationsTab recipient={updatedPatient} />,
			hidden: isPeptideOnly,
		},
		{
			key: "orders",
			label: "Orders",
			children: <OrdersTab patient={activePatient} />,
			hidden: isPeptideOnly,
		},
		{
			key: "Files",
			label: "Resources",
			children: <FilesTab patient={activePatient} setErrorMessages={setErrorMessages} />,
		},
		{
			key: "Supplements",
			label: "Supplements",
			children: <SupplementsTab patient={updatedPatient} company={company} />,
			disabled: !activePatient.id,
			hidden: isPeptideOnly,
		},
		{
			key: "Testing",
			label: "Testing",
			children: <TestingTab patient={updatedPatient} />,
		},
		{
			key: "TreatmentPlans",
			label: "Treatment Plans",
			children: <TreatmentPlanTab patient={updatedPatient} />,
			disabled: !activePatient.id,
		},
		{
			key: "Vitals",
			label: "Vitals",
			children: <VitalsTab patient={updatedPatient} />,
			disabled: !activePatient.id,
		},
	].filter((item) => !item.disabled && !item.hidden);

	return (
		<>
			<DetailDrawer
				open={isOpen}
				onClose={requestClose}
				width="85vw"
				title={""}
				titleMeta={patientInfoStrip} headerActions={
					<>
						{activePatient.id && (
							<Button icon={<ReloadOutlined />} onClick={requestRefresh} loading={isLoadingData}>
								Refresh
							</Button>
						)}

						<Button icon={<EditOutlined />} type="text" onClick={() => setShowNotes((v) => !v)} disabled={isSubmitting}>
							{showNotes ? "Hide Notes" : "Notes"}
						</Button>


						<Button type="primary" icon={<SaveOutlined />} onClick={handleSaveClick} loading={isSubmitting}>
							Save
						</Button>
					</>
				}
				tabs={tabItems}
				activeTab={activeTab}
				onTabChange={setActiveTab}
				tabLayout="sidebar"
				errorMessages={errorMessages}
				onClearErrors={() => setErrorMessages([])}
				footer={
					activePatient.id !== "" ? (
						<>
							<Button icon={<LockOutlined />} onClick={() => setPasswordModalOpen(true)} disabled={isSubmitting}>
								Reset Password
							</Button>
							<Button icon={<FileSyncOutlined />} onClick={() => setImportModalOpen(true)} disabled={isSubmitting || isExporting}>
								Import
							</Button>
							<Button
								icon={<DownloadOutlined />}
								loading={isExporting}
								disabled={isSubmitting}
								onClick={async () => {
									setIsExporting(true);
									try {
										await exportPatientXml(
											activePatient.uuid,
											activePatient.id,
											company?.name ?? getSessionUser().company,
										);
									} catch {
										showError('Export failed. Please try again.');
									} finally {
										setIsExporting(false);
									}
								}}
							>
								Export
							</Button>
							{activeTab === "Personal" && !updatedPatient.is_anonymous && (
								<Button danger={updatedPatient.is_active} onClick={onActiveClick} loading={isSubmitting}>
									{updatedPatient.is_active ? "Deactivate" : "Activate"}
								</Button>
							)}
						</>
					) : undefined
				}
			/>

			{/* Notes side drawer  */}
			{/* CU-14ygtkj65rw (HI_035_005845): Notes would overlay and clip the printed IHI summary, so it is left out of Billing prints only. */}
			<Drawer title="Patient Notes" placement="right" onClose={() => setShowNotes(false)} open={showNotes} width={400} mask={false} styles={{ body: { padding: 0 } }} zIndex={1001} rootClassName={activeTab === "Billing" ? "no-print" : undefined}>
				<Notes patient={activePatient} notes={notes} setNotes={setNotes} handleAutoSave={handleAutoSave} />
			</Drawer>


			{passwordModalOpen && <ResetPasswordeModal handleErrors={handleErrors} isOpen={passwordModalOpen} onClose={() => setPasswordModalOpen(false)} user={activePatient} />}
			<MedicalDirectorImportModal
				open={importModalOpen}
				onClose={() => setImportModalOpen(false)}
				mode="existing_patient"
				existingPatientUuid={activePatient.uuid}
				existingPatientName={`${activePatient.first_name} ${activePatient.last_name}`}
				onImportComplete={(_job: XmlImportJobStatus) => setImportModalOpen(false)}
			/>
			{showNotificationSelector && <NotificationSelectorDialog />}

			<Modal
				open={!!pendingPrimaryNameExit}
				title="Unsaved primary name"
				maskClosable={false}
				onCancel={() => setPendingPrimaryNameExit(null)}
				footer={[
					<Button key="keep" onClick={() => setPendingPrimaryNameExit(null)} disabled={isSubmitting}>
						Keep editing
					</Button>,
					<Button key="discard" danger onClick={handleUnsavedPrimaryNameDiscard} disabled={isSubmitting}>
						Discard changes
					</Button>,
					<Button key="save" type="primary" loading={isSubmitting} onClick={handleUnsavedPrimaryNameSave}>
						Save changes
					</Button>,
				]}
			>
				{pendingPrimaryNameExit === "replace"
					? "Another patient record is waiting to open. This patient's primary name has unsaved changes — save them, discard them, or keep editing and the other record will open once you have."
					: "This patient's primary name has unsaved changes. Save them, discard them, or keep editing."}
			</Modal>
		</>
	);
};

// CU-14ygtkje5r2: the one choke point for every render site; a customer identity never mounts the clinical modal.
const PatientModalGate: React.FC<PatientModalProps> = (props) =>
	isCustomerIdentity(props.patient) ? (
		<CustomerRecordDrawer open={props.isOpen} onClose={props.onClose} record={props.patient} />
	) : (
		<PatientModal {...props} />
	);

export default PatientModalGate;
